// What this tab knows of rematch proposals (design 14 §8.2) and of the end screen it shows: read every five seconds on
// any page, every two on an end screen, not at all while the tab is hidden. A proposal that ends is told for a few
// seconds to whoever had seen it open.

import { createEffect, createSignal, onCleanup } from "solid-js";
import * as api from "../api";
import type { Proposal, TableEnd } from "../api";

const SLOW_MS = 5_000;
const FAST_MS = 2_000;
const TOLD_MS = 8_000;

export interface Rematches {
  proposals: () => Proposal[];
  /** The proposals a bubble shows: open ones, and those that just ended for a moment. */
  bubbles: () => Proposal[];
  of: (token: string) => Proposal | undefined;
  /** The end screen's state; the page sets `at` while it shows one. */
  end: () => TableEnd | null;
  at: (token: string | null) => void;
  refresh: () => void;
  accept: (token: string) => Promise<api.TableView>;
  decline: (token: string) => void;
}

export function createRematches(enabled: () => boolean): Rematches {
  const [proposals, setProposals] = createSignal<Proposal[]>([]);
  const [end, setEnd] = createSignal<TableEnd | null>(null);
  const [at, setAt] = createSignal<string | null>(null);
  const [hidden, setHidden] = createSignal<string[]>([]); // ended ones already told
  const open = new Set<string>(); // the proposals this tab has seen open

  const take = (r: { proposals: Proposal[]; end: TableEnd | null }) => {
    for (const p of r.proposals) {
      if (!p.ended) open.add(p.token);
      else if (!open.has(p.token)) setHidden((h) => (h.includes(p.token) ? h : [...h, p.token]));
      else {
        open.delete(p.token);
        setTimeout(() => setHidden((h) => [...h, p.token]), TOLD_MS);
      }
    }
    setProposals(r.proposals);
    setEnd(r.end);
  };
  const refresh = () => {
    if (enabled() && !document.hidden) api.rematches(at()).then(take, () => {});
  };
  createEffect(() => {
    if (!enabled()) {
      setProposals([]);
      setEnd(null);
      return;
    }
    refresh();
    const id = setInterval(refresh, at() ? FAST_MS : SLOW_MS);
    const onShow = () => refresh();
    document.addEventListener("visibilitychange", onShow);
    onCleanup(() => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onShow);
    });
  });

  return {
    proposals,
    bubbles: () => proposals().filter((p) => p.ended !== "started" && !hidden().includes(p.token)),
    of: (token) => proposals().find((p) => p.token === token),
    end,
    at: (token) => {
      if (token === null) setEnd(null);
      setAt(token);
    },
    refresh,
    accept: (token) =>
      api.tableAction(token, "rematch").finally(refresh),
    decline: (token) => void api.tableAction(token, "rematch-decline").then(refresh, refresh),
  };
}
